import { useCallback, useEffect, useState } from 'react';
import { useAccount } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Ban, Check, ImageOff, Play, RotateCcw, Search, X } from 'lucide-react';

import {
  type ImageFingerprintListItem,
  type ImageFingerprintView,
  MailAutomationAPI,
} from '$api/MailAutomationAPI';
import { toWorkflowActor } from '$api/WorkflowsAPI';
import { TableHeadCustom, TablePaginationCustom } from '$components/table';
import { AdminShell } from '$layouts/v2/AdminShell';
import { useAccessToken } from '$src/authConfig';

import ImageFingerprintQuickReviewDialog from './ImageFingerprintQuickReviewDialog';

const reasonLabels: Record<string, string> = {
  signature_html_section: 'Signature section',
  signature_filename: 'Signature filename',
  generated_inline_filename: 'Generated filename',
  small_inline_image: 'Small inline image',
  unreferenced_inline_image: 'Not referenced',
  repeated_inline_image: 'Repeated image',
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const decisionColor = (
  item: ImageFingerprintListItem,
): 'error' | 'success' | 'warning' => {
  if (item.decision?.value === 'ignored') {
    return 'error';
  }
  if (item.decision?.value === 'allowed') {
    return 'success';
  }
  return 'warning';
};

function ImagePreview({
  fingerprintId,
  name,
  token,
}: {
  fingerprintId: string;
  name: string;
  token: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | undefined;
    setUrl(null);
    setUnavailable(false);
    setOpen(false);
    MailAutomationAPI.getImageFingerprintPreview(token, fingerprintId)
      .then((blob) => {
        if (!active) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) {
          setUnavailable(true);
        }
      });
    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fingerprintId, token]);

  let preview;
  if (url) {
    preview = (
      <Box
        component="img"
        src={url}
        alt={name}
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  } else if (unavailable) {
    preview = (
      <Tooltip title="Preview unavailable">
        <ImageOff size={18} />
      </Tooltip>
    );
  } else {
    preview = <CircularProgress size={18} />;
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        disabled={!url}
        aria-label={url ? `Open larger preview of ${name}` : undefined}
        onClick={() => setOpen(true)}
        sx={{
          width: 72,
          height: 52,
          p: 0,
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          color: 'text.secondary',
          cursor: url ? 'zoom-in' : 'default',
          '&:hover': url
            ? {
                borderColor: 'primary.main',
                boxShadow: 1,
              }
            : undefined,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
          '&:disabled': {
            opacity: 1,
          },
        }}
      >
        {preview}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        aria-label={`Image preview: ${name}`}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h6" noWrap>
            {name}
          </Typography>
          <IconButton
            aria-label="Close image preview"
            onClick={() => setOpen(false)}
            edge="end"
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            minHeight: 320,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'background.default',
          }}
        >
          {url && (
            <Box
              component="img"
              src={url}
              alt={name}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const headLabels = [
  { id: 'preview', label: 'Preview', width: 100 },
  { id: 'image', label: 'Image', minWidth: 260 },
  { id: 'evidence', label: 'Suggestion evidence', minWidth: 260 },
  { id: 'occurrences', label: 'Occurrences', width: 120 },
  { id: 'seen', label: 'Last seen', minWidth: 170 },
  { id: 'decision', label: 'Decision', width: 120 },
];

export default function ImageFingerprintsPage() {
  const { token } = useAccessToken();
  const account = useAccount();
  const [view, setView] = useState<ImageFingerprintView>('suggested');
  const [items, setItems] = useState<ImageFingerprintListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [minOccurrences, setMinOccurrences] = useState<number | undefined>();
  const [selected, setSelected] = useState<string[]>([]);
  const [quickReviewOpen, setQuickReviewOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await MailAutomationAPI.getImageFingerprints(token, {
        view,
        search,
        minOccurrences,
        page,
        rowsPerPage,
      });
      setItems(response.items);
      setTotalItems(response.totalItems);
      setSelected([]);
    } catch (loadError) {
      setError(String(loadError));
    } finally {
      setLoading(false);
    }
  }, [minOccurrences, page, rowsPerPage, search, token, view]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyDecision = async (decision: 'ignored' | 'allowed' | 'reset') => {
    if (!token || selected.length === 0) {
      return;
    }
    if (
      decision === 'ignored' &&
      !globalThis.confirm(
        `Ignore ${selected.length} selected inline image${
          selected.length === 1 ? '' : 's'
        } in future emails?`,
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await MailAutomationAPI.putImageFingerprintDecisions(
        token,
        selected.map((fingerprintId) => ({
          fingerprintId,
          decision,
          scope: 'inline' as const,
        })),
        toWorkflowActor(account),
      );
      setMessage(
        `${response.succeeded} image${
          response.succeeded === 1 ? '' : 's'
        } updated${
          response.failed ? `; ${response.failed} could not be updated` : ''
        }.`,
      );
      await load();
    } catch (saveError) {
      setError(String(saveError));
    } finally {
      setSaving(false);
    }
  };

  const selectedOnPage = items.filter((item) =>
    selected.includes(item.fingerprintId),
  ).length;

  return (
    <AdminShell title="Ignored images" titleIcon={<Ban />}>
      <Stack spacing={2}>
        <Alert severity="info">
          Suggestions continue through mail processing. Only images explicitly
          marked as ignored are filtered, and only when they are inline.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="success">{message}</Alert>}

        <Card>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{
              alignItems: { xs: 'stretch', md: 'center' },
              p: 2,
            }}
          >
            <TextField
              size="small"
              value={searchInput}
              placeholder="Search filename or SHA-256"
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setPage(0);
                  setSearch(searchInput.trim());
                }
              }}
              sx={{ flex: '1 1 320px', maxWidth: 520 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setPage(0);
                setSearch(searchInput.trim());
              }}
            >
              Search
            </Button>
            <TextField
              select
              size="small"
              label="Occurrences"
              value={minOccurrences ?? ''}
              onChange={(event) => {
                setMinOccurrences(
                  event.target.value === ''
                    ? undefined
                    : Number(event.target.value),
                );
                setPage(0);
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All occurrences</MenuItem>
              <MenuItem value={5}>5+</MenuItem>
              <MenuItem value={10}>10+</MenuItem>
              <MenuItem value={25}>25+</MenuItem>
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            {view === 'suggested' && (
              <Button
                variant="contained"
                startIcon={<Play size={16} />}
                disabled={loading || totalItems === 0}
                onClick={() => setQuickReviewOpen(true)}
              >
                Start swipen
              </Button>
            )}
            {view !== 'ignored' && (
              <Button
                color="error"
                variant="contained"
                startIcon={<Ban size={16} />}
                disabled={selected.length === 0 || saving}
                onClick={() => void applyDecision('ignored')}
              >
                Ignore selected
              </Button>
            )}
            {view !== 'allowed' && (
              <Button
                color="success"
                variant="outlined"
                startIcon={<Check size={16} />}
                disabled={selected.length === 0 || saving}
                onClick={() => void applyDecision('allowed')}
              >
                Allow selected
              </Button>
            )}
            {view === 'allowed' && (
              <Button
                variant="outlined"
                startIcon={<RotateCcw size={16} />}
                disabled={selected.length === 0 || saving}
                onClick={() => void applyDecision('reset')}
              >
                Reset selected
              </Button>
            )}
          </Stack>

          <Tabs
            value={view}
            onChange={(_event, nextView: ImageFingerprintView) => {
              setView(nextView);
              setPage(0);
              setSelected([]);
            }}
            sx={{
              px: 2,
              borderTop: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tab value="suggested" label="Suggested" />
            <Tab value="ignored" label="Ignored" />
            <Tab value="allowed" label="Allowed" />
          </Tabs>

          <TableContainer sx={{ minHeight: 280 }}>
            <Table size="small">
              <TableHeadCustom
                headLabel={headLabels}
                rowCount={items.length}
                numSelected={selectedOnPage}
                onSelectAllRows={(checked) =>
                  setSelected(
                    checked ? items.map((item) => item.fingerprintId) : [],
                  )
                }
              />
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
                      <Typography
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        No {view} image fingerprints found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  items.map((item) => {
                    const checked = selected.includes(item.fingerprintId);
                    const name =
                      item.sample?.name ?? 'Image sample unavailable';
                    return (
                      <TableRow
                        key={item.fingerprintId}
                        hover
                        selected={checked}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={checked}
                            onChange={() =>
                              setSelected((current) =>
                                checked
                                  ? current.filter(
                                      (value) => value !== item.fingerprintId,
                                    )
                                  : [...current, item.fingerprintId],
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {token && item.sample ? (
                            <ImagePreview
                              fingerprintId={item.fingerprintId}
                              name={name}
                              token={token}
                            />
                          ) : (
                            <ImageOff size={18} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {name}
                          </Typography>
                          <Tooltip title={item.fingerprintId}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                              }}
                            >
                              {item.fingerprintId.slice(0, 23)}…
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            sx={{
                              gap: 0.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            {item.suggestionReasons.map((reason) => (
                              <Chip
                                key={reason}
                                size="small"
                                variant="outlined"
                                label={reasonLabels[reason] ?? reason}
                              />
                            ))}
                          </Stack>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            Score {item.suggestionScore}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.occurrenceCount}</TableCell>
                        <TableCell>{formatDateTime(item.lastSeenAt)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={decisionColor(item)}
                            label={item.decision?.value ?? 'Suggested'}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePaginationCustom
            count={totalItems}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number.parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Card>

        {quickReviewOpen && token && (
          <ImageFingerprintQuickReviewDialog
            actor={toWorkflowActor(account)}
            minOccurrences={minOccurrences}
            onClose={() => {
              setQuickReviewOpen(false);
              void load();
            }}
            reasonLabels={reasonLabels}
            search={search}
            token={token}
          />
        )}
      </Stack>
    </AdminShell>
  );
}
